import React, { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useStaticQuery } from 'gatsby';
import { Button, Modal } from '@printdeal/ui-library';

import OrderDetailContext from '../../../contexts/OrderDetailContext';
import LineItem from '../../../../../../types/cart/LineItem';
import { QueryResult } from './types/FileDesignTypes';

import { getDesignServiceLineItem } from '../../../../../../helpers/artworkHelper';
import { DispatchCategoriesAccount } from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';

import RequestFileDesignForm from './RequestFileDesignForm';
import { findNodeByLocale } from '../../../../../../helpers/translations';

const query = graphql`
  fragment DesignOptionImage on ContentfulAsset {
    url
    width
    height
    title
  }

query {
  designServiceAssets: allContentfulDesignServiceAssets {
    nodes {
      node_locale
      colors {
        ...DesignOptionImage
      }
      templates {
        ...DesignOptionImage
      }
      fonts {
        ...DesignOptionImage
      }
      designDescription {
        designDescription
      }
      ideaDescription {
        ideaDescription
      }
      styleDescription {
        styleDescription
      }
      uploadFileDescription {
        uploadFileDescription
      }
    }
  }
}
`;

interface Props {
  lineItem: LineItem;
  handleCloseModal: (eventName: string) => void;
  dispatchEventCategory?: DispatchCategoriesAccount;
}

const RequestFileDesignModal = ({
  lineItem,
  handleCloseModal,
  dispatchEventCategory = 'order_detail_interaction',
}: Props) => {
  const intl = useIntl();
  const { order, reFetchOrder } = useContext(OrderDetailContext);
  const data = useStaticQuery<QueryResult>(query);
  const designServiceAssets = findNodeByLocale(data.designServiceAssets.nodes, intl.locale);
  const [isLoading, setIsLoading] = useState(false);

  if (!designServiceAssets) return null;

  const {
    colors, templates, fonts,
  } = designServiceAssets;

  if (!order
    || !reFetchOrder
    || colors.length < 1
    || templates.length < 1
    || fonts.length < 1) {
    // Abort, because something is not quite right.
    return null;
  }

  const serviceCustomLineItem = getDesignServiceLineItem(lineItem.id, order);

  // Abort if no service data is available.
  if (!serviceCustomLineItem) return null;

  return (
    <Modal
      isOpen
      size="xl"
      onClose={() => handleCloseModal('modal_file_design_close')}
      dismissLabel={intl.formatMessage({
        id: 'account.general.modal.close',
        defaultMessage: 'Close',
      })}
      title={intl.formatMessage({
        id: 'account.order.service.fileDesign.modal.header',
        defaultMessage: 'Have a design made',
      })}
    >
      <Modal.Content>
        <RequestFileDesignForm
          orderId={order.id}
          lineItemId={lineItem.id}
          serviceLineItemId={serviceCustomLineItem.id}
          colors={colors}
          templates={templates}
          fonts={fonts}
          designDescription={designServiceAssets.designDescription?.designDescription || ''}
          ideaDescription={designServiceAssets.ideaDescription?.ideaDescription || ''}
          styleDescription={designServiceAssets.styleDescription?.styleDescription || ''}
          uploadFileDescription={designServiceAssets.uploadFileDescription?.uploadFileDescription || ''}
          setIsLoading={setIsLoading}
          handleCloseModal={handleCloseModal}
          dispatchEventCategory={dispatchEventCategory}
        />
      </Modal.Content>
      <Modal.Footer className="pd-flex pd-flex-row-reverse pd-flex-wrap pd-gap-2">
        <Button
          type="submit"
          variant="primary"
          form="artworkService-form"
          isLoading={isLoading}
          disabled={isLoading}
        >
          <FormattedMessage
            id="account.order.service.any.modal.footer.submit"
            defaultMessage="Submit"
          />
        </Button>
        <Button
          variant="secondary"
          onClick={() => handleCloseModal('modal_file_design_close')}
          isLoading={isLoading}
          disabled={isLoading}
        >
          <FormattedMessage
            id="account.order.service.any.modal.footer.cancel"
            defaultMessage="Cancel"
          />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RequestFileDesignModal;
